import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { RootState, AppDispatch } from "../../../store";
import { fetchProjects } from "../../../store/slices/projectsSlice";
import ErrorMessage from "../../../components/base-components/Error";
import Loader from "../../../components/base-components/Loader";
import NoData from "../../../components/base-components/NoData";

const cardVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1 },
};

const MobileProjectsGrid: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { projects, status, error } = useSelector(
    (state: RootState) => state.projects
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [activeCardId, setActiveCardId] = useState<number | null>(null);

  const itemsPerPage = 9;
  const navigate = useNavigate();

  const baseUrl =
    process.env.REACT_APP_STRAPI_IMAGES_URL?.replace(/\/$/, "") || "";

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchProjects());
    }
  }, [dispatch, status]);

  if (status === "loading") {
    return <Loader />;
  }

  if (status === "failed") {
    return <ErrorMessage message={error} />;
  }

  if (!projects || projects.length === 0) {
    return <NoData message="No projects found" />;
  }

  const galleryItems = projects.map((project) => ({
    id: project.id,
    slug: project.attributes.slug,
    title: project.attributes.title,
    properties: project.attributes.properties,
    description:
      project.attributes.shortDescription || "Description not available.",
    imageUrl: project?.attributes?.titleImage?.data?.attributes?.formats?.small
      ?.url
      ? `${baseUrl}${project?.attributes?.titleImage?.data?.attributes?.formats?.small?.url}`
      : `${baseUrl}${project?.attributes?.titleImage?.data?.attributes.url}`,
  }));

  const totalPages = Math.ceil(galleryItems.length / itemsPerPage);
  const paginatedItems = galleryItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setActiveCardId(null);
  };

  const handleCardClick = (slug: string) => {
    navigate(`/developments/${slug}`);
  };

  return (
    <motion.section
      className="h-auto"
      initial="hidden"
      animate="visible"
      transition={{ staggerChildren: 0.2 }}
    >
      <div className="global-container py-[2rem] bg-white rounded-[0.5rem] mb-[15rem] md:mb-[7rem] h-full flex flex-col">
        <div
          className="grid gap-[1.6rem] justify-center items-center"
          style={{
            gridTemplateColumns: "repeat(auto-fill, minmax(35rem, 1fr))",
          }}
        >
          {paginatedItems.map((item) => (
            <motion.div
              key={item.id}
              layout
              className="relative h-[30rem] rounded-[0.5rem] shadow-lg cursor-pointer overflow-hidden group"
              onClick={() => handleCardClick(item.slug)}
              variants={cardVariants}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <img
                src={item.imageUrl}
                alt={item.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />

              <motion.div
                className={`absolute left-0 right-0 bottom-0 bg-[rgba(0, 0, 0, 0.50)] backdrop-blur-md text-white p-[1.5rem] border border-transparent rounded-[0.5rem] transition-opacity duration-500 opacity-100`}
                initial={{ opacity: 1, translateY: 0 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
              >
                <div>
                  <h6 className="text-white font-semibold text-[2rem]/[2rem] mb-[0.3rem]">
                    {item.title}
                  </h6>
                  <p className="text-white mb-[0.3rem] text-[1.2rem]">
                    {item.description}
                  </p>
                  {item.properties && (
                    <div className="flex flex-wrap gap-2 max-h-[3.6rem] overflow-hidden">
                      {item.properties
                        .split(",")
                        .map((property) => property.trim())
                        .slice(0, 6)
                        .map((property, index) => (
                          <span
                            key={index}
                            className="bg-white bg-opacity-20 text-white text-[1rem] p-[0.8rem] rounded-[0.5rem]"
                          >
                            {property}
                          </span>
                        ))}
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-between items-center gap-4 mt-[2rem]">
            <p className="text-black">
              Page {currentPage} of {totalPages}
            </p>

            <div className="flex">
              {currentPage !== 1 && (
                <button
                  className="mr-[1.2rem]"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    fill="none"
                  >
                    <rect
                      x="40"
                      y="40"
                      width="40"
                      height="40"
                      rx="5"
                      transform="rotate(-180 40 40)"
                      fill="white"
                    />
                    <path
                      d="M25 10L15 20L25 30"
                      stroke="black"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
              {currentPage !== totalPages && (
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    fill="none"
                  >
                    <rect width="40" height="40" rx="5" fill="white" />
                    <path
                      d="M15 30L25 20L15 10"
                      stroke="black"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </motion.section>
  );
};

export default MobileProjectsGrid;
